'use client';
import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLayerLock } from '../../lib/useLayerLock';
import { useT } from '../../lib/i18n';
import { Icon } from '../Icon';
import { won } from '../course/price';
import { cycleLabel, type BillingPlan } from './types';

/**
 * 구독 플랜 슬라이더 — 가이드 `subscribe.html` 그대로.
 *
 * 한 화면에 **3장(1024↓ 2장 · 640↓ 1장)**, 그보다 적으면 화살표·도트를 감춘다
 * (가이드가 `no-slide` 클래스로 감춘다 — 우리도 같은 클래스를 붙인다).
 * 넘김은 `translateX(index × (카드폭 + 간격))` — 가이드 스크립트와 같은 계산이다.
 *
 * 카드 안의 주기 라디오는 **그 카드 안에서만** 배타여야 한다(`name` 에 플랜 id 를 넣는다).
 * 고른 주기의 금액이 아래 [선택 금액]과 [구독하기] 링크에 함께 반영된다.
 */
const ICON_PREV = 'chevron-left';
const ICON_NEXT = 'chevron-right';
const ICON_INFO = 'info';

const AUTOPLAY_MS = 5000;

/** 스와이프로 칠 최소 거리(px) — 이보다 짧으면 탭이나 흔들림으로 본다 */
const SWIPE_MIN = 40;

/** 지원 기기 표기 — 가이드는 `PC+Mobile` 처럼 적는다 */
const TOOL_KEY: Record<string, string> = {
  ALL: 'sub.tool.all', PC: 'sub.tool.pc', MOBILE: 'sub.tool.mobile',
};

function perView(w: number) {
  if (w <= 640) return 1;
  if (w <= 1024) return 2;
  return 3;
}

export function PlanSlider({ plans }: { plans: BillingPlan[] }) {
  const t = useT();
  const wrapRef = useRef<HTMLDivElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);
  const [index, setIndex] = useState(0);
  const [view, setView] = useState(3);
  const [offset, setOffset] = useState(0);
  const [paused, setPaused] = useState(false);

  /** 플랜마다 고른 주기 — 처음에는 첫 줄(관리자가 넣은 차례의 맨 위) */
  const [picked, setPicked] = useState<Record<number, number>>(() =>
    Object.fromEntries(plans.map((p) => [p.id, p.cycles[0]?.key ?? 0])));

  const max = Math.max(0, plans.length - view);
  const sliding = plans.length > view;

  /*
   * 카드 폭은 CSS(`flex: 0 0 calc(...)`)가 정하므로 **재어서** 쓴다.
   * 창 크기가 바뀌면 폭도 간격도 달라지니 그때마다 다시 잰다 — 가이드와 같은 방식이다.
   */
  const measure = useCallback(() => {
    setView(perView(window.innerWidth));
    const track = trackRef.current;
    const first = track?.firstElementChild as HTMLElement | null;
    if (!track || !first) return;
    const gap = parseFloat(getComputedStyle(track).gap) || 0;
    setOffset(first.getBoundingClientRect().width + gap);
  }, []);

  useEffect(() => {
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [measure]);

  // 창이 넓어져 카드가 다 보이게 되면 index 가 범위를 넘는다 — 되돌린다
  useEffect(() => { setIndex((i) => Math.min(i, Math.max(0, plans.length - view))); }, [view, plans.length]);

  /*
   * 자동 넘김 — 마우스를 올리면 멈춘다(가이드). **모션을 줄이는 설정이면 아예 안 돈다**
   * (프론트 공용셋 §8-1 #7 — 배너 슬라이더와 같은 규칙).
   */
  useEffect(() => {
    if (!sliding || paused) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const timer = setInterval(() => setIndex((i) => (i >= max ? 0 : i + 1)), AUTOPLAY_MS);
    return () => clearInterval(timer);
  }, [sliding, paused, max]);

  /*
   * 손가락으로 넘기기(2026-09-03 사용자 지시) — 휴대폰에는 화살표를 누를 만한 자리가 없다.
   * 가이드 스크립트에도 없어 우리가 붙인다.
   *
   * **세로 스크롤을 뺏지 않는다** — 가로 이동이 세로보다 클 때만 넘긴다. 손가락을 비스듬히
   * 움직이면 그건 페이지를 굴리려는 것이지 카드를 넘기려는 것이 아니다.
   */
  const touchStart = useRef<{ x: number; y: number } | null>(null);
  const onTouchStart = (e: React.TouchEvent) => {
    setPaused(true); // 자동 넘김 멈춤 — 손이 닿으면 사람이 고르는 중이다
    const p0 = e.touches[0];
    touchStart.current = { x: p0.clientX, y: p0.clientY };
  };
  const onTouchEnd = (e: React.TouchEvent) => {
    const from = touchStart.current;
    touchStart.current = null;
    if (!from || !sliding) return;
    const p1 = e.changedTouches[0];
    const dx = p1.clientX - from.x;
    const dy = p1.clientY - from.y;
    if (Math.abs(dx) < SWIPE_MIN || Math.abs(dx) <= Math.abs(dy)) return;
    setIndex((i) => (dx < 0 ? Math.min(max, i + 1) : Math.max(0, i - 1)));
  };

  const priceOf = (p: BillingPlan) => p.cycles.find((c) => c.key === picked[p.id])?.price ?? 0;

  return (
    <div className={`tc-slider-wrap${sliding ? '' : ' no-slide'}`} ref={wrapRef}
      onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)}
      onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
      <div className="tc-slider-viewport">
        <div className="tc-slider-track" ref={trackRef}
          style={{ transform: `translateX(-${sliding ? index * offset : 0}px)` }}>
          {plans.map((p) => (
            <div className="sp-slide-item" key={p.id}>
              <div className="sp-card">
                {p.badges.length > 0 && (
                  <div className="sp-card-badge">
                    <span className="badge badge-pill badge-primary">{p.badges[0]}</span>
                  </div>
                )}
                <p className="sp-card-title">{p.name}</p>
                <div className="sp-card-divider"></div>

                {/* 주기 고르기 — 가이드 `course-price-item`(강좌 단가 목록과 같은 조각) */}
                <div className="course-price-option">
                  <div className="course-price-list">
                    {p.cycles.map((c) => (
                      <label key={c.key} className={`course-price-item${picked[p.id] === c.key ? ' active' : ''}`}>
                        <input type="radio" name={`plan-cycle-${p.id}`} value={c.key}
                          checked={picked[p.id] === c.key}
                          onChange={() => setPicked((s) => ({ ...s, [p.id]: c.key }))} />
                        <span className="price-label">{cycleLabel(c, t)}</span>
                        <span className="price-val">{won(c.price)}{t('prd.money')}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <PlanInfo plan={p} />

                <div className="sp-card-cta">
                  <div className="sp-card-price-row">
                    <span className="sp-card-price-label">{t('sub.pickedPrice')}</span>
                    <span className="sub-info-price sp-card-price-val">{won(priceOf(p))}{t('prd.money')}</span>
                  </div>
                  {/*
                    구독하기 — 고른 주기를 주소에 실어 결제 화면으로 넘긴다.
                    주기 키는 서버가 참조하는 값이라 그대로 넘긴다(순번이 아니다).
                  */}
                  <a className="action-btn action-submit btn-lg"
                    href={`/subscribe/checkout?pid=${p.id}&cycle=${picked[p.id]}`}>
                    {t('sub.subscribe')}
                  </a>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <button type="button" className="tc-slider-btn tc-slider-prev" aria-label={t('action.prev')}
        disabled={index <= 0} onClick={() => setIndex((i) => Math.max(0, i - 1))}>
        <Icon name={ICON_PREV} />
      </button>
      <button type="button" className="tc-slider-btn tc-slider-next" aria-label={t('action.next')}
        disabled={index >= max} onClick={() => setIndex((i) => Math.min(max, i + 1))}>
        <Icon name={ICON_NEXT} />
      </button>
      <div className="tc-slider-dots">
        {sliding && Array.from({ length: max + 1 }, (_, i) => (
          <button key={i} type="button" className={`tc-slider-dot${i === index ? ' active' : ''}`}
            aria-label={t('sub.goSlide', { n: String(i + 1) })} onClick={() => setIndex(i)} />
        ))}
      </div>
    </div>
  );
}

/**
 * 카드 아래 정보표 — 대상 상품 · 지원 기기 · 등록대수 · 초기화 횟수.
 * 값은 **구독 가입 시 스냅샷되는 그 값**이다(§3-0) — 회원 기본값이 아니라 이 플랜의 것이다.
 *
 * 대상 상품이 여럿이면 **[i] 버튼 → 모달**로 전부 보여 준다(2026-09-07 가이드 개정 반영).
 * 전에는 물음표에 얹는 툴팁(`tooltip-wrap`)이었다 — 목록이 길면 화면을 덮고, 휴대폰에서는
 * 손가락으로 띄울 수도 없다. 가이드가 `sp-info-btn` + `sp-include-modal` 로 바꿨다.
 */
function PlanInfo({ plan }: { plan: BillingPlan }) {
  const t = useT();
  const { target } = plan;
  /*
   * 모달은 **눌러야 열린다** — 그 시점은 이미 브라우저라 `document.body` 가 있다.
   * 그래서 다른 화면들이 쓰는 `mounted` 빗장이 필요 없다(효과에서 상태를 바꾸지 않는다).
   */
  const [openList, setOpenList] = useState(false);
  useLayerLock(openList, () => setOpenList(false));
  /*
   * 대상 상품 — 종류 지정이면 종류 이름을, 개별 지정이면 **첫 상품 + 나머지 개수**를 적고
   * 전체 목록은 물음표에 얹는다(가이드 `tooltip-wrap`). 개수가 많아도 카드가 안 늘어난다.
   */
  const names = target.products.map((x) => x.name);
  /*
   * **비어 있을 수 있다**(실측 확인 — 종류를 하나도 안 고른 구독 상품이 있었다).
   * 그때 `join` 은 빈 문자열이라 칸이 통째로 빈다 — 빈칸은 "아직 안 채운 값" 처럼 보이므로
   * 아무것도 안 열린다는 뜻을 글자로 적는다(이관 규칙 §3-1 의 "빈 값 → 행 0개" 와 같은 상태다).
   */
  const label = target.kind === 'PTYPE'
    ? (target.ptypes.length === 0
      ? t('sub.targetNone')
      : target.ptypes.map((x) => t(`prd.type.${x.toLowerCase()}`)).join(', '))
    : names.length === 0 ? t('sub.targetNone')
      : names.length === 1 ? names[0]
        : t('sub.targetMore', { first: names[0], n: String(names.length - 1) });

  /** 전부 보여 줄 목록 — 개별 지정이고 둘 이상일 때만 있다(종류 지정은 펼칠 것이 없다) */
  const fullList = target.kind === 'PICK' && names.length > 1 ? names : null;

  const rows: { label: string; value: string; more?: boolean }[] = [
    { label: t('sub.target'), value: label, more: !!fullList },
    { label: t('sub.tool'), value: t(TOOL_KEY[plan.toolUse] ?? 'sub.tool.all') },
  ];
  // 기기 대수는 그 갈래를 쓸 때만 적는다 — PC 전용 플랜에 모바일 대수를 적으면 오해한다
  if (plan.toolUse !== 'MOBILE')
    rows.push({ label: t('sub.devicePc'), value: t('sub.deviceCount', { n: String(plan.toolPc ?? 0) }) });
  if (plan.toolUse !== 'PC')
    rows.push({ label: t('sub.deviceMobile'), value: t('sub.deviceCount', { n: String(plan.toolMobile ?? 0) }) });
  rows.push({ label: t('sub.reset'), value: t('sub.resetCount', { n: String(plan.deviceResetLimit ?? 0) }) });

  return (
    <div className="sub-info-grid">
      {rows.map((r) => (
        <div className="sub-info-row" key={r.label}>
          <span className="sub-info-label">{r.label}</span>
          <span className="sub-info-val">
            {r.value}
            {r.more && (
              <button type="button" className="sp-info-btn" aria-label={t('sub.targetAll')}
                onClick={() => setOpenList(true)}>
                <Icon name={ICON_INFO} />
              </button>
            )}
          </span>
        </div>
      ))}

      {/*
        포함 강좌 모달 — 가이드 `sp-include-modal` 그대로(제목 줄·표 머리·[확인]).
        **딤을 눌러 닫지 않는다**(우리 공용 규칙) — 닫는 길은 [확인]과 Esc 뿐이다.
      */}
      {openList && fullList && createPortal(
        <>
          <div className="modal-backdrop open"></div>
          <div className="modal sp-include-modal open">
            <div className="modal-body">
              <div className="sp-include-head">
                <span className="sp-include-bar"></span>
                <p className="sp-include-title">{t('sub.targetOf', { name: plan.name })}</p>
              </div>
              <div className="sp-include-box">
                <div className="sp-include-list-head">{t('sub.targetCourse')}</div>
                {fullList.map((n) => <div className="sp-include-item" key={n}>{n}</div>)}
              </div>
              <button type="button" className="sp-include-confirm" onClick={() => setOpenList(false)}>
                {t('action.confirm')}
              </button>
            </div>
          </div>
        </>,
        document.body,
      )}
    </div>
  );
}
